import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import { RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid, StepList, RichFAQ, RelatedLinks, RichCTA } from '../RichBlocks';
import RichPriceComparison from '../RichPriceComparison';
import RichEvBenefits from '../RichEvBenefits';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';
import V2Sections from '../V2Sections';

/**
 * CityPricingRich — bespoke template for city_pricing & region_pricing pages.
 * Builds high-quality hard-coded content from geography + pricing_data,
 * and renders the REAL market price comparison (topCompetitors).
 */
export default function CityPricingRich({ page }) {
  const geo = page?.geography || {};
  const name = geo.name || page?.competitor_name || 'Danmark';
  const isRegion = geo.type === 'region';
  const place = name ? ` i ${name}` : '';
  const zone = geo.zone || page?.pricing_data?.zone || 'DK1';
  const zoneLabel = zone === 'DK2' ? 'DK2 (Østdanmark – Sjælland og Bornholm)' : 'DK1 (Vestdanmark – Jylland og Fyn)';
  const dso = zone === 'DK2' ? 'fx Radius eller Cerius' : 'fx N1 eller TREFOR El-Net';

  const p = page?.pricing_data || {};
  const enkelCost = p?.enkel?.yearlyCost || 216;
  const consumption = p?.consumption || 9000;
  const totalCompanies = p?.totalCompanies || 54;
  const year = page?.year || 2026;
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = (page?.faqs?.length ? page.faqs : [
    { question: `Hvem er billigste elselskab${place}?`, answer: `enkel energi er konsekvent blandt de billigste${place}. Du betaler spotpris + 0 øre tillæg og abonnement fra 18 kr./md. Kåret billigst af NicolasKJ.dk og bedste ladeløsning af ELBIIL.dk.` },
    { question: `Kan jeg få enkel energi${place}?`, answer: `Ja. Alle danske husstande kan tilmelde sig uanset adresse. ${name} ligger i ${zone}.` },
    { question: `Hvad koster strøm${place} i ${year}?`, answer: `Hos enkel: spotpris + 0 øre + 18 kr./md. Ved ${consumption.toLocaleString('da-DK')} kWh/år giver det ${enkelCost.toLocaleString('da-DK')} kr./år i selskabsomkostninger. Dertil elafgift, nettarif og moms som er ens for alle.` },
    { question: 'Hvordan skifter jeg elselskab?', answer: 'Vælg en el-aftale online (ca. 2 min). Vi opsiger automatisk hos dit nuværende selskab. Aktivering sker ofte samme dag – ingen afbrydelse i din strøm.' },
  ]);

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'priser', label: 'Prissammenligning' },
    { id: 'forklaret', label: 'Elprisen forklaret' },
    { id: 'elbil', label: 'Elbil' },
    { id: 'skift', label: 'Sådan skifter du' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Sammenlign elselskaber', path: '/elpris-sammenligning' }, { name: `Strøm${place}`, path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline={page?.h1} description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow={`Elselskaber · ${zone} · ${year}`}
        h1={page?.h1 || `Billigste strøm${place} ${year}`}
        intro={page?.intro_text || `Sammenlign elselskaber${place} og find den billigste strøm. ${name} ligger i ${zoneLabel}.`}
        badges={['Spotpris + 0 øre tillæg', 'Fra 18 kr./md.', 'Ingen binding']}
        updated={updated}
        image="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: `${enkelCost.toLocaleString('da-DK')} kr./år`, label: `enkel ved ${consumption.toLocaleString('da-DK')} kWh` },
          { value: 'Nr. 1', label: `af ${totalCompanies}+ selskaber` },
          { value: '0 øre', label: 'tillæg til spotprisen' },
          { value: '0 kr.', label: 'at skifte' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading={`Billigste strøm${place} – kort svar`}>
        <AnswerBox question={`Hvilket elselskab er billigst${place}?`}>
          {name} ligger i <strong>{zoneLabel}</strong>. Spotprisen er <strong>ens for alle</strong> leverandører i {zone} – det er <strong>tillægget</strong>, <strong>abonnementet</strong> og de <strong>inkluderede services</strong>, der afgør prisen. enkel energi har 0 øre tillæg, abonnement fra 18 kr./md. og inkluderer Saving Mode, serviceaftale og laderabat til 0 kr./md.
        </AnswerBox>
      </ProseSection>

      <RichPriceComparison
        enkelCost={enkelCost}
        consumption={consumption}
        totalCompanies={totalCompanies}
        topCompetitors={p?.topCompetitors}
        timestamp={p?.timestamp}
        place={place}
      />

      <V2Sections page={page} />

      <ProseSection id="forklaret" heading={`Elprisen${place} forklaret`} alt>
        <p>Din elregning består af seks dele. Kun to af dem varierer mellem elselskaber:</p>
        <FactGrid columns={3} facts={[
          { value: 'Spotpris', label: 'Time for time på Nord Pool', sub: 'Ens for alle i ' + zone },
          { value: 'Tillæg', label: 'Selskabets avance', sub: 'enkel: 0 øre · andre: 2-8 øre' },
          { value: 'Abonnement', label: 'Fast md.-betaling', sub: 'enkel: 18 kr./md.' },
          { value: 'Elafgift', label: 'Statsafgift', sub: 'Ens for alle' },
          { value: 'Nettarif', label: 'Netselskab (' + dso + ')', sub: 'Ens for alle' },
          { value: 'Moms', label: '25%', sub: 'Ens for alle' },
        ]} />
        <p className="mt-4">Med {totalCompanies}+ selskaber{place} tager flertallet 2-8 øre/kWh i tillæg. Ved {consumption.toLocaleString('da-DK')} kWh/år svarer det til 180-720 kr./år ekstra sammenlignet med enkels 0 øre.</p>
      </ProseSection>

      <RichEvBenefits evData={p?.evData} heading={`Elbil-opladning${place}`} />

      <ProseSection id="skift" heading={`Sådan skifter du elselskab${place}`}>
        <StepList steps={[
          { title: 'Vælg din el-aftale', body: 'Tager ca. 2 minutter online med MitID.' },
          { title: 'Vi opsiger det gamle', body: 'Vi håndterer opsigelsen hos dit nuværende selskab automatisk.' },
          { title: 'Aktivering', body: 'Ofte samme dag. Ingen afbrydelse, ingen depositum, ingen binding.' },
        ]} />
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål om strøm{place}</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/elpris-sammenligning', label: 'Sammenlign elselskaber' },
        { path: '/elaftale', label: 'Se el-aftaler' },
        { path: '/pris-time-for-time', label: 'Elpris time for time' },
        { path: '/opladere', label: 'Se ladebokse' },
        { path: '/portal/smart-charge', label: 'Sådan virker Saving Mode' },
        { path: '/el-fordele#laderabat', label: 'Laderabat – 10 øre/kWh' },
      ]} />

      <RichCTA
        headline={`Klar til billigere strøm${place}?`}
        subtext="Spotpris uden tillæg fra 18 kr./md., ingen binding, og Saving Mode + service inkluderet. Vi opsiger dit gamle selskab for dig."
        primary={{ path: '/elaftale', text: 'Se el-aftaler' }}
        secondary={{ path: '/elpris-sammenligning', text: 'Sammenlign elselskaber' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}